import React, { useEffect, useState, useRef } from 'react';
import Navbar from './components/Navbar';
import PageCard from './components/PageCard';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CEPMaskInput from './components/masks/CEPMaskInput';
import { formatCEP, unformatCEP } from './utils/cepUtils';
import { formatCPF, isValidCPF, unformatCPF } from './utils/cpfUtils';
import CPFMaskInput from './components/masks/CPFMaskInput';
import Footer from './components/Footer';
import useDefaultModal from './components/hooks/useDefaultModal';
import DefaultModal from './components/modals/DefaultModal';
import { compressFoto, blobSizeKb } from './utils/compressFoto';

// Certifique-se de que o CSS para .full-screen-div ainda está sendo importado
// .full-screen-div { display: flex; flex-direction: column; min-height: 100vh; }

const Pessoa = () => {
  // ... todo o seu state, useEffects e funções (NÃO MUDA NADA AQUI) ...
  const [formData, setFormData] = useState({
    cnpjAssociacao: '',
    cpf: '',
    id: '',
    nome: '',
    dataNasc: '',
    sexo: '',
    estadoCivil: '',
    nomePai: '',
    nomeMae: '',
    rgNum: '',
    rgDataEmissao: '',
    rgUf: '',
    rgEmissor: '',
    nacionalidade: '',
    profissao: '',
    cpfConjuge: '',
    nomeConjuge: '',
    logrNome: '',
    logrNum: '',
    logrCompl: '',
    logrBairro: '',
    logrCep: '',
    logrCidade: '',
    logrUf: '',
    dataFiliacao: '',
  });
  const [cpfError, setCpfError] = useState('');
  const [ufs, setUfs] = useState([]);
  const [ufRg, setUfRg] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [emissor, setEmissor] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const refs = useRef({}); // For CEPMaskInput ref
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoRemover, setFotoRemover] = useState(false);
  const fotoInputRef = useRef(null);

  const loadFoto = (cnpjPessoa, id) => {
    axios
      .get(`${API_BASE_URL.PESSOA}/foto`, {
        params: { cnpjAssociacao: cnpjPessoa, id },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      .then(result => {
        setFotoPreview(URL.createObjectURL(result.data));
        setFotoRemover(false);
      })
      .catch(error => {
        if (error.response && error.response.status !== 404) {
          console.error('Erro ao carregar a foto:', error);
        }
      });
  };

  const handleFotoChange = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const blob = await compressFoto(file);
      if (fotoPreview && fotoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(fotoPreview);
      }
      setFotoFile(blob);
      setFotoPreview(URL.createObjectURL(blob));
      setFotoRemover(false);
      showDefaultModal('success', `Foto preparada (${blobSizeKb(blob)} KB). Salve o cadastro para enviá-la.`);
    } catch (error) {
      showDefaultModal('error', error.message || 'Erro ao processar a foto.');
    }
  };

  const handleRemoveFoto = () => {
    if (fotoPreview && fotoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(fotoPreview);
    }
    setFotoPreview(null);
    setFotoFile(null);
    setFotoRemover(true);
  };

  useEffect(() => {
    axios
      .get(`${API_BASE_URL.EMISSOR}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setEmissor(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter listas Emissor:', error);
      });
    axios
      .get(`${API_BASE_URL.UF}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setUfRg(result.data);
        setUfs(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter listas UF:', error);
      });

    if (location.state && location.state.pessoa) {
      setFormData(prev => ({
        ...prev,
        ...location.state.pessoa,
        cnpjAssociacao: cnpj,
      }));
      if (location.state.pessoa.id) {
        loadFoto(cnpj, location.state.pessoa.id);
      }
    } else {
      setFormData(prev => ({ ...prev, cnpjAssociacao: cnpj }));
    }
  }, [location.state, token, cnpj]);

  useEffect(() => {
    if (formData.logrUf) {
      axios
        .get(`${API_BASE_URL.MUNICIPIO}/uf/${formData.logrUf}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setCidades(result.data);
        })
        .catch(error => {
          console.error('Erro ao carregar cidades:', error);
        });
    } else {
      setCidades([]);
    }
  }, [formData.logrUf, token]);

  const handleChange = e => {
    const { name, value } = e.target;

    // Campos que NÃO devem ser convertidos para maiúsculas
    const excludeFromUppercase = ['cpf', 'logrCep', 'rgNum', 'logrNum']; // Adicione outros se necessário

    const processedValue = excludeFromUppercase.includes(name)
      ? value
      : typeof value === 'string'
      ? value.toUpperCase()
      : value;

    if (name === 'sexo') {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: processedValue,
        estadoCivil: '',
        nacionalidade: '',
      }));
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: processedValue,
      }));
    }
  };

  const getEstadoCivilOptions = () => {
    const isFeminino = formData.sexo === 'FEMININO';
    return [
      {
        value: isFeminino ? 'CASADA' : 'CASADO',
        label: isFeminino ? 'CASADA' : 'CASADO',
      },
      {
        value: isFeminino ? 'DIVORCIADA' : 'DIVORCIADO',
        label: isFeminino ? 'DIVORCIADA' : 'DIVORCIADO',
      },
      {
        value: isFeminino ? 'SOLTEIRA' : 'SOLTEIRO',
        label: isFeminino ? 'SOLTEIRA' : 'SOLTEIRO',
      },
      {
        value: isFeminino ? 'VIÚVA' : 'VIÚVO',
        label: isFeminino ? 'VIÚVA' : 'VIÚVO',
      },
    ];
  };

  const getNacionalidadeOptions = () => {
    const isFeminino = formData.sexo === 'FEMININO';
    return [
      {
        value: isFeminino ? 'BRASILEIRA' : 'BRASILEIRO',
        label: isFeminino ? 'BRASILEIRA' : 'BRASILEIRO',
      },
      {
        value: isFeminino ? 'NATURALIZADA' : 'NATURALIZADO',
        label: isFeminino ? 'NATURALIZADA' : 'NATURALIZADO',
      },
      {
        value: isFeminino ? 'ESTRANGEIRA' : 'ESTRANGEIRO',
        label: isFeminino ? 'ESTRANGEIRA' : 'ESTRANGEIRO',
      },
    ];
  };

  const getCargoDiretoriaOptions = () => {
    const isFeminino = formData.sexo === 'FEMININO';
    return [
      { value: 'PRESIDENTE', label: 'PRESIDENTE' },
      { value: 'VICE-PRESIDENTE', label: 'VICE-PRESIDENTE' },
      {
        value: isFeminino ? 'SECRETÁRIA' : 'SECRETÁRIO',
        label: isFeminino ? 'SECRETÁRIA' : 'SECRETÁRIO',
      },
      {
        value: isFeminino ? 'TESOUREIRA' : 'TESOUREIRO',
        label: isFeminino ? 'TESOUREIRA' : 'TESOUREIRO',
      },
    ];
  };

  const getCargoDeliberativoOptions = () => {
    const isFeminino = formData.sexo === 'FEMININO';
    return [
      { value: 'PRESIDENTE', label: 'PRESIDENTE' },
      {
        value: isFeminino ? 'SECRETÁRIA' : 'SECRETÁRIO',
        label: isFeminino ? 'SECRETÁRIA' : 'SECRETÁRIO',
      },
      {
        value: isFeminino ? 'CONSELHEIRA' : 'CONSELHEIRO',
        label: isFeminino ? 'CONSELHEIRA' : 'CONSELHEIRO',
      },
      { value: 'MEMBRO EFETIVO', label: 'MEMBRO EFETIVO' },
      { value: 'MEMBRO SUPLENTE', label: 'MEMBRO SUPLENTE' },
    ];
  };

  const getCargoFiscalOptions = () => {
    return [
      { value: 'MEMBRO EFETIVO', label: 'MEMBRO EFETIVO' },
      { value: 'MEMBRO SUPLENTE', label: 'MEMBRO SUPLENTE' },
    ];
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!isValidCPF(formData.cpf)) {
      // setCpfError('CPF inválido');
      showDefaultModal('error', 'CPF inválido');
      return;
    }
    // setCpfError('');

    const dataToSave = {
      ...formData,
      cpf: unformatCPF(formData.cpf),
      logrCep: unformatCEP(formData.logrCep),
    };
    console.log('Data to save:', dataToSave); // Verifique os dados antes de enviar
    axios
      .post(`${API_BASE_URL.PESSOA}/save`, dataToSave, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        const idSalvo = result.data?.id;
        const cnpjSalvo = formData.cnpjAssociacao;
        const finalizar = () =>
          showDefaultModal('success', 'Registro salvo com sucesso!', () =>
            navigate('/pessoas')
          );

        if (fotoRemover && idSalvo) {
          axios
            .delete(`${API_BASE_URL.PESSOA}/foto`, {
              params: { cnpjAssociacao: cnpjSalvo, id: idSalvo },
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(finalizar)
            .catch(() => {
              showDefaultModal('error', 'Registro salvo, mas houve erro ao remover a foto.');
            });
          return;
        }

        if (fotoFile && idSalvo) {
          const formDataFoto = new FormData();
          formDataFoto.append('file', fotoFile, 'foto.jpg');
          formDataFoto.append('cnpjAssociacao', cnpjSalvo);
          formDataFoto.append('id', idSalvo);
          axios
            .post(`${API_BASE_URL.PESSOA}/foto`, formDataFoto, {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`,
              },
            })
            .then(() => {
              finalizar();
            })
            .catch(() => {
              showDefaultModal('error', 'Registro salvo, mas houve erro ao enviar a foto.');
            });
          return;
        }

        finalizar();
      })
      .catch(error => {
        console.error('Erro ao salvar formData:', error);
      });
  };

  return (
    <PageCard
      title="Cadastro de Pessoa"
      icon="bi-person"
      onClose={() => navigate('/pessoas')}
      footer={
        <div className="d-flex gap-2 justify-content-end">
          <button type="submit" form="pessoa-form" className="btn btn-success btn-sm">
            Salvar
          </button>
          <Link className="btn btn-secondary btn-sm" to="/pessoas">
            Cancelar
          </Link>
        </div>
      }
    >
      <form id="pessoa-form" onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-12 col-md-3 col-xl-2">
            <div className="border rounded p-3 text-center h-100" style={{ backgroundColor: '#fafafa' }}>
              <div
                className="mx-auto"
                style={{
                  width: 120,
                  height: 160,
                  overflow: 'hidden',
                  borderRadius: 8,
                  border: '2px solid #e9ecef',
                  backgroundColor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 0 6px rgba(0,0,0,0.05)',
                }}
              >
                {fotoPreview ? (
                  <img
                    src={fotoPreview}
                    alt="Foto do associado"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <i className="bi bi-person" style={{ fontSize: '3rem', color: '#adb5bd' }}></i>
                )}
              </div>
              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFotoChange}
              />
              <div className="d-grid gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success"
                  onClick={() => fotoInputRef.current?.click()}
                >
                  <i className="bi bi-camera me-1"></i>
                  {fotoPreview ? 'Trocar Foto' : 'Selecionar Foto'}
                </button>
                {fotoPreview && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={handleRemoveFoto}
                  >
                    <i className="bi bi-trash me-1"></i>
                    Remover
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-9 col-xl-10">
            <div className="d-flex align-items-center mb-2">
              <span className="text-uppercase small fw-semibold text-secondary me-2">Dados Pessoais</span>
              <span className="flex-grow-1" style={{ borderTop: '1px dashed #ced4da' }}></span>
            </div>
            <div className="row g-2 small">
          {/* CPF */}
          <div className="col-12 col-sm-6 col-md-3 input-group-sm">
            <label htmlFor="cpf" className="form-label mb-1">
              CPF
            </label>
            <CPFMaskInput
              id="cpf"
              value={formatCPF(formData.cpf)}
              onChange={e =>
                handleChange({
                  target: {
                    name: 'cpf',
                    value: unformatCPF(e.target.value),
                  },
                })
              }
              name="cpf"
              type="text"
            />
            {cpfError && (
              <small className="text-danger">{cpfError}</small>
            )}
          </div>
          {/* Nome */}
          <div className="col-12 col-sm-6 col-md-6 input-group-sm">
            <label htmlFor="nome" className="form-label mb-1">
              Nome
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="nome"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
            />
          </div>
          {/* Data de Nascimento */}
          <div className="col-12 col-sm-6 col-md-3 input-group-sm">
            <label htmlFor="dataNasc" className="form-label mb-1">
              Data de Nascimento
            </label>
            <input
              type="date"
              className="form-control"
              id="dataNasc"
              name="dataNasc"
              value={formData.dataNasc}
              onChange={handleChange}
            />
          </div>
          {/* Sexo */}
          <div className="col-12 col-sm-6 col-md-2 input-group-sm">
            <label htmlFor="sexo" className="form-label mb-1">
              Sexo
            </label>
            <select
              className="form-control"
              name="sexo"
              value={formData.sexo}
              onChange={handleChange}
            >
              <option value="">Selecione...</option>
              <option value="FEMININO">FEMININO</option>
              <option value="MASCULINO">MASCULINO</option>
            </select>
          </div>

          {/* Estado Civil */}
          <div className="col-12 col-sm-6 col-md-3 input-group-sm">
            <label htmlFor="estadoCivil" className="form-label mb-1">
              Estado Civil
            </label>
            <select
              className="form-control"
              name="estadoCivil"
              value={formData.estadoCivil}
              onChange={handleChange}
              disabled={!formData.sexo}
            >
              <option value="">Selecione...</option>
              {formData.sexo &&
                getEstadoCivilOptions().map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-12 mb-2 mt-3 d-flex align-items-center">
            <span className="text-uppercase small fw-semibold text-secondary me-2">Filiação</span>
            <span className="flex-grow-1" style={{ borderTop: '1px dashed #ced4da' }}></span>
          </div>
          {/* Nome do Pai */}
          <div className="col-12 col-sm-6 col-md-4 input-group-sm">
            <label htmlFor="nomePai" className="form-label mb-1">
              Nome do Pai
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="nomePai"
              name="nomePai"
              value={formData.nomePai}
              onChange={handleChange}
            />
          </div>
          {/* Nome da Mãe */}
          <div className="col-12 col-sm-6 col-md-4 input-group-sm">
            <label htmlFor="nomeMae" className="form-label mb-1">
              Nome da Mãe
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="nomeMae"
              name="nomeMae"
              value={formData.nomeMae}
              onChange={handleChange}
            />
          </div>

          <div className="col-12 mb-2 mt-3 d-flex align-items-center">
            <span className="text-uppercase small fw-semibold text-secondary me-2">Documentação</span>
            <span className="flex-grow-1" style={{ borderTop: '1px dashed #ced4da' }}></span>
          </div>
          {/* Nacionalidade */}
          <div className="col-12 col-sm-6 col-md-3 input-group-sm">
            <label htmlFor="nacionalidade" className="form-label mb-1">
              Nacionalidade
            </label>
            <select
              className="form-control"
              name="nacionalidade"
              value={formData.nacionalidade}
              onChange={handleChange}
              disabled={!formData.sexo}
            >
              <option value="">Selecione...</option>
              {formData.sexo &&
                getNacionalidadeOptions().map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </div>

          {/* RG */}
          <div className="col-12 col-sm-6 col-md-2 input-group-sm">
            <label htmlFor="rgNum" className="form-label mb-1">
              Nº RG
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="rgNum"
              name="rgNum"
              value={formData.rgNum}
              onChange={handleChange}
            />
          </div>
          {/* Data Emissão RG */}
          <div className="col-12 col-sm-6 col-md-2 input-group-sm">
            <label htmlFor="rgDataEmissao" className="form-label mb-1">
              Data Emissão RG
            </label>
            <input
              type="date"
              className="form-control"
              id="rgDataEmissao"
              name="rgDataEmissao"
              value={formData.rgDataEmissao}
              onChange={handleChange}
            />
          </div>

          {/* EMISSOR */}
          <div className="col-12 col-sm-6 col-md-3 input-group-sm">
            <label htmlFor="rgEmissor" className="form-label mb-1">
              Emissor RG
            </label>
            <select
              id="rgEmissor"
              className="form-control small"
              value={formData.rgEmissor}
              onChange={handleChange}
              name="rgEmissor"
            >
              <option value="">Selecione...</option>
              {emissor.map((e, index) => (
                <option key={index} value={e.codigo}>
                  {e.codigo}
                </option>
              ))}
            </select>
          </div>
          {/* RG UF */}
          <div className="col-12 col-sm-2 col-md-1 input-group-sm">
            <label htmlFor="rgUf" className="form-label mb-1">
              UF RG
            </label>
            <select
              id="rgUf"
              className="form-control small"
              value={formData.rgUf}
              onChange={handleChange}
              name="rgUf"
            >
              <option value="">Selecione...</option>
              {ufRg.map((e, index) => (
                <option key={index} value={e.sigla}>
                  {e.sigla}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 mb-2 mt-3 d-flex align-items-center">
            <span className="text-uppercase small fw-semibold text-secondary me-2">Endereço</span>
            <span className="flex-grow-1" style={{ borderTop: '1px dashed #ced4da' }}></span>
          </div>
          {/* Logradouro */}
          <div className="col-12 col-sm-6 col-md-4 input-group-sm">
            <label htmlFor="logrNome" className="form-label mb-1">
              Logradouro Nome
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="logrNome"
              name="logrNome"
              value={formData.logrNome}
              onChange={handleChange}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-1 input-group-sm">
            <label htmlFor="logrNum" className="form-label mb-1">
              Número
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="logrNum"
              name="logrNum"
              value={formData.logrNum}
              onChange={handleChange}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-3 input-group-sm">
            <label htmlFor="logrCompl" className="form-label mb-1">
              Complemento
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="logrCompl"
              name="logrCompl"
              value={formData.logrCompl}
              onChange={handleChange}
            />
          </div>

          <div className="col-12 col-sm-6 col-md-4 input-group-sm">
            <label htmlFor="logrBairro" className="form-label mb-1">
              Bairro
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="logrBairro"
              name="logrBairro"
              value={formData.logrBairro}
              onChange={handleChange}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-1 input-group-sm">
            <label className="form-label mb-1">UF</label>
            <select
              className="form-control small"
              value={formData.logrUf}
              onChange={handleChange}
              name="logrUf"
            >
              <option value="">Selecione uma UF...</option>
              {ufs.map((uf, index) => (
                <option key={index} value={uf.sigla}>
                  {uf.sigla}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-6 input-group-sm">
            <label className="form-label mb-1">Cidade</label>
            <select
              className="form-control"
              value={formData.logrCidade}
              onChange={handleChange}
              name="logrCidade"
              disabled={!formData.logrUf}
            >
              <option value="">Selecione uma cidade...</option>
              {cidades.map((cidade, index) => (
                <option key={index} value={cidade.nome}>
                  {cidade.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-2 input-group-sm">
            <label className="form-label mb-1">CEP</label>
            <CEPMaskInput
              value={formatCEP(formData.logrCep)}
              onChange={e =>
                handleChange({
                  target: {
                    name: 'logrCep',
                    value: unformatCEP(e.target.value),
                  },
                })
              }
              name="logrCep"
              ref={el => (refs.current.cep = el)}
              type="text"
            />
          </div>

          <div className="col-12 mb-2 mt-3 d-flex align-items-center">
            <span className="text-uppercase small fw-semibold text-secondary me-2">Profissão e Cônjuge</span>
            <span className="flex-grow-1" style={{ borderTop: '1px dashed #ced4da' }}></span>
          </div>

          <div className="col-12 col-sm-6 col-md-4 input-group-sm">
            <label htmlFor="profissao" className="form-label mb-1">
              Profissão / Ocupação
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="profissao"
              name="profissao"
              value={formData.profissao}
              onChange={handleChange}
            />
          </div>
          {/* CPF Cônjuge */}
          <div className="col-12 col-sm-6 col-md-2 input-group-sm">
            <label htmlFor="cpfConjuge" className="form-label mb-1">
              CPF do Cônjuge
            </label>
            <CPFMaskInput
              id="cpfConjuge"
              value={formatCPF(formData.cpfConjuge)}
              onChange={e =>
                handleChange({
                  target: {
                    name: 'cpfConjuge',
                    value: unformatCPF(e.target.value),
                  },
                })
              }
              name="cpfConjuge"
              type="text"
            />
          </div>
          {/* Nome Cônjuge */}
          <div className="col-12 col-sm-6 col-md-4 input-group-sm">
            <label htmlFor="nomeConjuge" className="form-label mb-1">
              Nome do Cônjuge
            </label>
            <input
              type="text"
              className="form-control text-uppercase"
              id="nomeConjuge"
              name="nomeConjuge"
              value={formData.nomeConjuge}
              onChange={handleChange}
            />
          </div>
          <div className="col-12 mb-2 mt-3 d-flex align-items-center">
            <span className="text-uppercase small fw-semibold text-secondary me-2">Associação</span>
            <span className="flex-grow-1" style={{ borderTop: '1px dashed #ced4da' }}></span>
          </div>
          {/* Data de Filiação */}
          <div className="col-12 col-sm-6 col-md-3 input-group-sm">
            <label htmlFor="dataFiliacao" className="form-label mb-1">
              Data de Filiação
            </label>
            <input
              type="date"
              className="form-control"
              id="dataFiliacao"
              name="dataFiliacao"
              value={formData.dataFiliacao}
              onChange={handleChange}
            />
          </div>
          </div>
      </div>
      </div>

      </form>

      <DefaultModal {...setDefaultModal} />
    </PageCard>
  );
};

export default Pessoa;
